import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, TablePagination, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Tooltip, Menu, MenuItem,
  CircularProgress, InputAdornment, Alert, Divider, Switch,
  Stack, useMediaQuery, useTheme, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EventIcon from '@mui/icons-material/Event';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import { getDoctors, createDoctor, updateDoctor, deleteDoctor, getDoctorSchedule, updateDoctorSchedule } from '../services/api';
import { useApiCall } from '../hooks/useApiCall';

const emptyDoctor = { name: '', specialty: '', phone: '', email: '', matricula_provincial: '', matricula_nacional: '', default_duration_minutes: 30 };
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

// Un médico puede tener matrícula provincial, nacional, o ambas — d.matricula
// (campo único legado) se muestra solo si no cargó ninguna de las dos nuevas.
const fmtMatricula = (d) => {
  const parts = [];
  if (d.matricula_provincial) parts.push(`MP ${d.matricula_provincial}`);
  if (d.matricula_nacional) parts.push(`MN ${d.matricula_nacional}`);
  if (parts.length === 0 && d.matricula) parts.push(d.matricula);
  return parts.join(' · ');
};

const DAY_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const defaultSchedule = () =>
  DAY_NAMES.map((_, i) => ({ day_of_week: i, enabled: i < 5, time_start: '08:00', time_end: '18:00' }));

const Doctors = () => {
  const apiCall = useApiCall();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [doctors, setDoctors] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const searchTimer = useRef(null);
  const [showInactive, setShowInactive] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [doctorData, setDoctorData] = useState(emptyDoctor);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuDoctor, setMenuDoctor] = useState(null);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDoctor, setScheduleDoctor] = useState(null);
  const [scheduleData, setScheduleData] = useState(defaultSchedule());
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleError, setScheduleError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDoctors({
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        ...(search ? { search } : {}),
        is_active: !showInactive,
      });
      setDoctors(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      setDoctors([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, showInactive]);

  useEffect(() => { load(); }, [load]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(0);
      setSearch(val);
    }, 350);
  };

  const set = (f) => (e) => setDoctorData(p => ({ ...p, [f]: e.target.value }));

  const openCreate = () => {
    setIsEdit(false);
    setDoctorData(emptyDoctor);
    setFormError('');
    setOpenDialog(true);
  };

  const openEdit = (d) => {
    setIsEdit(true);
    setSelectedDoctor(d);
    setDoctorData({
      name: d.name, specialty: d.specialty || '', phone: d.phone || '', email: d.email || '',
      matricula_provincial: d.matricula_provincial || '', matricula_nacional: d.matricula_nacional || '',
      default_duration_minutes: d.default_duration_minutes,
    });
    setFormError('');
    setOpenDialog(true);
    closeMenu();
  };

  const handleSubmit = async () => {
    if (!doctorData.name.trim()) { setFormError('El nombre es obligatorio'); return; }
    setSaving(true);
    setFormError('');
    try {
      if (isEdit) await updateDoctor(selectedDoctor.id, doctorData);
      else await createDoctor(doctorData);
      setOpenDialog(false);
      await load();
    } catch (err) {
      setFormError(err.userMessage || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (d) => {
    // Es un soft-delete (is_active=False), no un borrado real — su historia
    // clínica, recetas y turnos pasados quedan intactos y siguen mostrando
    // su nombre correctamente. Mismo criterio ya aplicado a pacientes: dar
    // de baja a un médico que dejó la clínica, no "eliminarlo" del sistema.
    if (!window.confirm(`¿Dar de baja al Dr. ${d.name}? No va a aparecer para asignar turnos/consultas nuevas, pero su historial existente queda intacto.`)) return;
    try {
      await apiCall(deleteDoctor(d.id), 'Error al dar de baja al médico');
      await load();
    } catch {}
    closeMenu();
  };

  const handleReactivate = async (d) => {
    try {
      await apiCall(updateDoctor(d.id, { is_active: true }), 'Error al reactivar el médico');
      await load();
    } catch {}
    closeMenu();
  };

  const openMenu = (e, d) => { setMenuAnchor(e.currentTarget); setMenuDoctor(d); };
  const closeMenu = () => { setMenuAnchor(null); setMenuDoctor(null); };

  const openSchedule = async (d) => {
    closeMenu();
    setScheduleDoctor(d);
    setScheduleError('');
    const base = defaultSchedule();
    try {
      const res = await getDoctorSchedule(d.id);
      const slots = res.data || [];
      slots.forEach(s => {
        base[s.day_of_week] = { day_of_week: s.day_of_week, enabled: true, time_start: s.time_start, time_end: s.time_end };
      });
    } catch {}
    setScheduleData(base);
    setScheduleOpen(true);
  };

  const handleSaveSchedule = async () => {
    const active = scheduleData.filter(d => d.enabled);
    const invalid = active.find(d => d.time_start >= d.time_end);
    if (invalid) {
      setScheduleError(`Hora inicio debe ser anterior a hora fin (${DAY_NAMES[invalid.day_of_week]})`);
      return;
    }
    setScheduleSaving(true);
    setScheduleError('');
    try {
      await updateDoctorSchedule(scheduleDoctor.id, active.map(({ day_of_week, time_start, time_end }) => ({ day_of_week, time_start, time_end })));
      setScheduleOpen(false);
    } catch (err) {
      setScheduleError(err.userMessage || 'Error al guardar horario');
    } finally {
      setScheduleSaving(false);
    }
  };

  const setSlot = (i, field, value) =>
    setScheduleData(prev => prev.map((d, idx) => idx === i ? { ...d, [field]: value } : d));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">Doctores</Typography>
          {total > 0 && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{total} profesional{total !== 1 ? 'es' : ''}</Typography>}
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Nuevo doctor
        </Button>
      </Box>

      {/* Búsqueda */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <TextField
          sx={{ flex: 1, minWidth: 200 }}
          placeholder="Buscar por nombre o especialidad…"
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} /></InputAdornment>,
          }}
        />
        <FormControlLabel
          sx={{ ml: 0, flexShrink: 0 }}
          control={
            <Switch
              size="small"
              checked={showInactive}
              onChange={e => { setShowInactive(e.target.checked); setPage(0); }}
            />
          }
          label={<Typography variant="body2" color="text.secondary">Ver dados de baja</Typography>}
        />
      </Paper>

      {loading ? (
        <Paper sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} />
        </Paper>
      ) : doctors.length === 0 ? (
        <Paper sx={{ py: 8, textAlign: 'center' }}>
          <MedicalServicesOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1, display: 'block', mx: 'auto' }} />
          <Typography color="text.secondary">
            {search ? 'No se encontraron doctores con ese criterio' : 'No hay doctores registrados'}
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {doctors.map(d => (
            <Paper key={d.id} sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={600}>Dr. {d.name}</Typography>
                    {d.is_active === false && <Chip label="Dado de baja" size="small" color="default" sx={{ height: 18, fontSize: '0.65rem' }} />}
                  </Box>
                  {d.specialty && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{d.specialty}</Typography>}
                  {fmtMatricula(d) && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{fmtMatricula(d)}</Typography>}
                  {d.phone && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{d.phone}</Typography>}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Turnos de {d.default_duration_minutes} min</Typography>
                </Box>
                <IconButton size="small" onClick={(e) => openMenu(e, d)} sx={{ flexShrink: 0 }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          ))}
          <TablePagination
            component={Paper}
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Filas:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Especialidad</TableCell>
                <TableCell>Matrícula</TableCell>
                <TableCell>Teléfono</TableCell>
                <TableCell>Duración turno</TableCell>
                <TableCell align="right" sx={{ width: 60 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {doctors.map(d => (
                <TableRow key={d.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={600}>Dr. {d.name}</Typography>
                      {d.is_active === false && <Chip label="Dado de baja" size="small" color="default" sx={{ height: 18, fontSize: '0.65rem' }} />}
                    </Box>
                  </TableCell>
                  <TableCell>{d.specialty || <Typography variant="caption" color="text.disabled">—</Typography>}</TableCell>
                  <TableCell>{fmtMatricula(d) || <Typography variant="caption" color="text.disabled">—</Typography>}</TableCell>
                  <TableCell>{d.phone || <Typography variant="caption" color="text.disabled">—</Typography>}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{d.default_duration_minutes} min</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Acciones">
                      <IconButton size="small" onClick={(e) => openMenu(e, d)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Filas:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </TableContainer>
      )}

      {/* Menú de acciones */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}
        PaperProps={{ sx: { minWidth: 160 } }}>
        <MenuItem dense onClick={() => openEdit(menuDoctor)}>Editar</MenuItem>
        <MenuItem dense onClick={() => openSchedule(menuDoctor)}>
          <EventIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
          Horario semanal
        </MenuItem>
        <Divider />
        {menuDoctor?.is_active === false ? (
          <MenuItem dense sx={{ color: 'success.main' }} onClick={() => handleReactivate(menuDoctor)}>Reactivar</MenuItem>
        ) : (
          <MenuItem dense sx={{ color: 'error.main' }} onClick={() => handleDeactivate(menuDoctor)}>Dar de baja</MenuItem>
        )}
      </Menu>

      {/* Dialog horario semanal */}
      <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Horario semanal — Dr. {scheduleDoctor?.name}</DialogTitle>
        <DialogContent>
          {scheduleError && <Alert severity="error" sx={{ mb: 2 }}>{scheduleError}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Activá los días que el médico atiende y definí el rango horario. Los turnos fuera de este horario serán rechazados.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {scheduleData.map((slot, i) => (
              <Box key={i} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                p: 1.5, borderRadius: 2,
                bgcolor: slot.enabled ? 'rgba(21,101,192,0.05)' : '#f8fafc',
                border: '1px solid',
                borderColor: slot.enabled ? 'rgba(21,101,192,0.25)' : '#e2e8f0',
                opacity: slot.enabled ? 1 : 0.6,
                transition: 'all 0.15s',
              }}>
                <Switch
                  size="small"
                  checked={slot.enabled}
                  onChange={e => setSlot(i, 'enabled', e.target.checked)}
                />
                <Typography variant="body2" fontWeight={600} sx={{ width: 90, flexShrink: 0 }}>
                  {DAY_NAMES[i]}
                </Typography>
                <TextField
                  type="time"
                  size="small"
                  label="Desde"
                  value={slot.time_start}
                  onChange={e => setSlot(i, 'time_start', e.target.value)}
                  disabled={!slot.enabled}
                  sx={{ width: 130 }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                />
                <Typography variant="body2" color="text.secondary">—</Typography>
                <TextField
                  type="time"
                  size="small"
                  label="Hasta"
                  value={slot.time_end}
                  onChange={e => setSlot(i, 'time_end', e.target.value)}
                  disabled={!slot.enabled}
                  sx={{ width: 130 }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveSchedule} disabled={scheduleSaving}>
            {scheduleSaving ? <CircularProgress size={18} /> : 'Guardar horario'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog crear/editar */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{isEdit ? `Editar — Dr. ${selectedDoctor?.name}` : 'Nuevo doctor'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <TextField label="Nombre completo *" fullWidth autoFocus value={doctorData.name} onChange={set('name')} />
          <TextField label="Especialidad" fullWidth value={doctorData.specialty} onChange={set('specialty')} />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              label="Matrícula provincial"
              fullWidth
              value={doctorData.matricula_provincial}
              onChange={set('matricula_provincial')}
            />
            <TextField
              label="Matrícula nacional"
              fullWidth
              value={doctorData.matricula_nacional}
              onChange={set('matricula_nacional')}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            Alcanza con cargar una de las dos para poder emitir recetas electrónicas.
          </Typography>
          <TextField label="Teléfono" fullWidth value={doctorData.phone} onChange={set('phone')} />
          <TextField label="Email" fullWidth value={doctorData.email} onChange={set('email')} />
          <TextField
            label="Duración del turno (minutos)"
            type="number"
            fullWidth
            value={doctorData.default_duration_minutes}
            onChange={e => setDoctorData(p => ({ ...p, default_duration_minutes: Number(e.target.value) }))}
            inputProps={{ min: 5, max: 120, step: 5 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving || !doctorData.name.trim()}>
            {saving ? <CircularProgress size={18} /> : isEdit ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Doctors;
